import { useState } from 'react'
import { Table, Button, Modal, Form, Input, DatePicker, Switch, Tag, message } from 'antd'
import { PlusOutlined, EditOutlined, CalendarOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { countdownApi, type Countdown } from '../services/countdowns'

const Countdown = () => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingCountdown, setEditingCountdown] = useState<Countdown | null>(null)
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  // Fetch countdowns data
  const { data: countdownsResponse, isLoading } = useQuery({
    queryKey: ['countdowns'],
    queryFn: countdownApi.getCountdowns,
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Create countdown mutation
  const createCountdownMutation = useMutation({
    mutationFn: countdownApi.createCountdown,
    onSuccess: (response) => {
      message.success(response.message || 'Countdown created successfully')
      queryClient.invalidateQueries({ queryKey: ['countdowns'] })
      setIsModalVisible(false)
      form.resetFields()
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create countdown')
    },
  })

  // Update countdown mutation
  const updateCountdownMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      countdownApi.updateCountdown(id, data),
    onSuccess: (response) => {
      message.success(response.message || 'Countdown updated successfully')
      queryClient.invalidateQueries({ queryKey: ['countdowns'] })
      setIsModalVisible(false)
      form.resetFields()
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update countdown')
    },
  })

  // Toggle countdown status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => 
      status === 'active' ? countdownApi.deactivateCountdown(id) : countdownApi.activateCountdown(id),
    onSuccess: (response) => {
      message.success(response.message || 'Countdown status updated successfully')
      queryClient.invalidateQueries({ queryKey: ['countdowns'] })
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update countdown status')
    },
  })

  const countdownsData = countdownsResponse?.data || []

  const handleAddCountdown = () => {
    setEditingCountdown(null)
    form.resetFields()
    setIsModalVisible(true)
  }

  const handleEditCountdown = (record: Countdown) => {
    setEditingCountdown(record)
    form.setFieldsValue({
      title: record.title,
      date: dayjs(record.date, 'DD/MM/YYYY')
    })
    setIsModalVisible(true)
  }

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      const countdownData = {
        title: values.title,
        date: values.date.format('DD/MM/YYYY'),
      
      }

      if (editingCountdown) {
        // Update existing countdown
        updateCountdownMutation.mutate({
          id: editingCountdown._id,
          data: countdownData
        })
      } else {
        // Create new countdown
        createCountdownMutation.mutate(countdownData)
      }
    })
  }

  const handleModalCancel = () => {
    setIsModalVisible(false)
    form.resetFields()
  }

  const handleToggleStatus = (record: Countdown) => {
    toggleStatusMutation.mutate({
      id: record._id,
      status: record.status
    })
  }

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text: string) => (
        <span className="font-medium text-gray-900">{text}</span>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (text: string) => (
        <span className="text-gray-700">
          {dayjs(text, 'DD/MM/YYYY').format('DD/MM/YYYY')}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag 
          color={status === 'active' ? "green" : "red"} 
          className="!text-xs !rounded-full !px-2 !py-1"
        >
          {status === 'active' ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Countdown) => (
        <div className="flex items-center gap-3">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleEditCountdown(record)
            }}
            className="text-gray-500 hover:text-primary p-1"
          />
          <Switch
            checked={record.status === 'active'}
            onChange={() => handleToggleStatus(record)}
            loading={toggleStatusMutation.isPending}
            className="bg-primary"
          />
        </div>
      ),
    },
  ]

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">Countdown</h1>
          {countdownsData.length === 0 && (
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAddCountdown}
              className="bg-primary hover:bg-primary/90 h-10"
            >
              Add Countdown
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1">
        <Table
          columns={columns}
          dataSource={countdownsData.map((countdown) => ({
            ...countdown,
            key: countdown._id
          }))}
          pagination={false}
          className="countdown-table"
          rowClassName="hover:bg-gray-50"
          loading={isLoading}
        />
      </div>

      {/* Edit/Add Modal */}
      <Modal
        title={editingCountdown ? "Edit countdown" : "Add countdown"}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Save Changes"
        cancelText="Cancel"
        className="countdown-modal"
        okButtonProps={{
          className: "bg-primary hover:bg-primary/90",
          loading: createCountdownMutation.isPending || updateCountdownMutation.isPending
        }}
      >
        <Form
          form={form}
          layout="vertical"
          className="mt-6"
        >
          <Form.Item
            label={<span className="text-sm font-medium text-gray-700">Title*</span>}
            name="title"
            rules={[{ required: true, message: 'Please enter title' }]}
            required={false}
          >
            <Input 
              placeholder="Enter title of countdown"
              className="h-10 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
            />
          </Form.Item>

          <Form.Item
            label={<span className="text-sm font-medium text-gray-700">Date*</span>}
            name="date"
            rules={[{ required: true, message: 'Please select date' }]}
            required={false}
          >
            <DatePicker
              placeholder="DD/MM/YYYY"
              format="DD/MM/YYYY"
              
              className="w-full h-10 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
              suffixIcon={<CalendarOutlined className="text-gray-400" />}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Countdown
